/**
 * Capital gains tax for shares (listed/unlisted) and real estate.
 *
 * Holding-period thresholds:
 *  - Listed shares (individual): 365 days for long-term
 *  - Land/building (individual): 5 years (1825 days) for long-term
 *
 * @example
 * calculateCapitalGains({
 *   asset: "shares-listed-individual",
 *   gain: 100000,
 *   holdingDays: 400,
 *   ownerType: "individual",
 *   listed: true,
 * });
 * // { tax: 5000, rate: 0.05, ... }
 */

import { getRates } from "./data/index.js";
import type {
  CapitalGainsAsset,
  FiscalYear,
} from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** Options for `calculateCapitalGains` — high-level form. */
export interface CapitalGainsOptions {
  /** Realised gain in NPR. */
  readonly gain: number;
  /**
   * Asset class.
   *  - `"shares-listed"`: ordinary listed shares (individual: 7.5% short / 5% long, entity: 10%).
   *  - `"shares-listed-promoter"`: promoter shares — flat 10% regardless of holding period.
   *  - `"shares-unlisted"`: unlisted/private shares (10% individual, 15% entity).
   *  - `"land"`: real estate (individual: 7.5% short / 5% long over 5y, entity: 1.5%).
   *
   * For **bonus shares**, pass `gain = sellPrice * qty` (cost basis = 0) and
   * `asset: "shares-listed"` — the rate then follows the holding period.
   */
  readonly asset:
    | "shares-listed"
    | "shares-listed-promoter"
    | "shares-unlisted"
    | "land";
  /** Owner type. Ignored for `"shares-listed-promoter"` (always individual flat rate). */
  readonly ownerType: "individual" | "entity";
  /** Holding period in days (only relevant for individuals on listed shares / land). */
  readonly holdingDays?: number;
  /** Fiscal year (default current). */
  readonly fiscalYear?: FiscalYear;
}

/** Result of capital gains calculation. */
export interface CapitalGainsResult {
  readonly fiscalYear: FiscalYear;
  readonly asset: CapitalGainsAsset;
  readonly gain: number;
  readonly rate: number;
  readonly tax: number;
  readonly netProceeds: number;
}

const SHARES_LONG_TERM_DAYS = 365;
const LAND_LONG_TERM_DAYS = 5 * 365; // 1825

/** Resolve the canonical `CapitalGainsAsset` key from high-level inputs. */
function resolveAssetKey(opts: CapitalGainsOptions): CapitalGainsAsset {
  const { asset, ownerType, holdingDays = 0 } = opts;
  if (asset === "shares-listed-promoter") {
    return "shares-listed-promoter";
  }
  if (asset === "shares-listed") {
    if (ownerType === "entity") return "shares-listed-entity";
    return holdingDays >= SHARES_LONG_TERM_DAYS
      ? "shares-listed-individual-long"
      : "shares-listed-individual-short";
  }
  if (asset === "shares-unlisted") {
    return ownerType === "entity"
      ? "shares-unlisted-entity"
      : "shares-unlisted-individual";
  }
  // land
  if (ownerType === "entity") return "land-entity";
  return holdingDays >= LAND_LONG_TERM_DAYS
    ? "land-individual-long"
    : "land-individual-short";
}

/** Calculate capital gains tax (high-level form). */
export function calculateCapitalGains(opts: CapitalGainsOptions): CapitalGainsResult {
  ensureNonNegative(opts.gain, "calculateCapitalGains");
  const rates = getRates(opts.fiscalYear);
  const key = resolveAssetKey(opts);
  const rate = rates.capitalGains[key];
  const tax = r2(opts.gain * rate);
  return {
    fiscalYear: rates.fiscalYear,
    asset: key,
    gain: r2(opts.gain),
    rate,
    tax,
    netProceeds: r2(opts.gain - tax),
  };
}

/** Get the rate for a specific canonical asset key. */
export function getCapitalGainsRate(
  asset: CapitalGainsAsset,
  fiscalYear?: FiscalYear,
): number {
  return getRates(fiscalYear).capitalGains[asset];
}
